/**
 * The "Need a hand?" popover behind the header's Help sticker.
 *
 * The popover itself is native (`popover="auto"`: top layer, light dismiss, Escape), anchored by
 * assets/anchored-popover.js. The Popover API adds no ARIA state and moves no focus, so this
 * module fills those gaps for every help panel on the page (there is one in the header and the
 * same list is repeated inside the menu sheet's foot, which is not a popover):
 *  - mirrors `aria-expanded` onto the trigger on every toggle,
 *  - moves focus to the first link when the panel opens and back to the trigger when it closes
 *    with focus still inside (Escape), so keyboard users never land on <body>,
 *  - closes the panel when a theme drawer (the bag) opens, since both would otherwise stack in
 *    the top layer in show order,
 *  - reveals the "Chat with us" row only once Shopify Inbox has actually registered
 *    <shopify-chat>. Without the app the row stays hidden: no fake chat.
 *
 * Listeners are on the document (capture, because `toggle` does not bubble) so a section
 * re-render in the theme editor cannot orphan them.
 */
const PANEL_SELECTOR = '.hdr-help__panel';

/**
 * @param {HTMLElement} panel
 * @returns {HTMLElement | null}
 */
function triggerFor(panel) {
  const trigger = panel.closest('anchored-popover-component')?.querySelector(`[popovertarget="${CSS.escape(panel.id)}"]`);
  return trigger instanceof HTMLElement ? trigger : null;
}

/**
 * First link or button that is actually rendered (hidden rows are skipped).
 * offsetParent is null for everything in the top layer, so visibility is read from client rects.
 * @param {HTMLElement} panel
 * @returns {HTMLElement | null}
 */
function firstFocusable(panel) {
  for (const candidate of panel.querySelectorAll('a[href], button')) {
    if (candidate instanceof HTMLElement && candidate.getClientRects().length > 0) return candidate;
  }
  return null;
}

document.addEventListener(
  'toggle',
  (event) => {
    const panel = event.target;
    if (!(panel instanceof HTMLElement) || !panel.matches(PANEL_SELECTOR)) return;
    if (!(event instanceof ToggleEvent)) return;

    const trigger = triggerFor(panel);
    const isOpen = event.newState === 'open';
    trigger?.setAttribute('aria-expanded', String(isOpen));

    if (isOpen) {
      firstFocusable(panel)?.focus({ preventScroll: true });
    } else if (document.activeElement === document.body || panel.contains(document.activeElement)) {
      trigger?.focus({ preventScroll: true });
    }
  },
  true
);

// Keyboard focus leaving the panel (Tab past its last row) closes it: the Popover API's light
// dismiss reacts to pointer and Escape only, and an open panel would otherwise float over the
// page while focus moves on underneath it. relatedTarget is null on a pointer light-dismiss or a
// click on the trigger, both of which the browser already handles.
document.addEventListener(
  'focusout',
  (event) => {
    const panel = event.target instanceof Element ? event.target.closest(PANEL_SELECTOR) : null;
    if (!(panel instanceof HTMLElement)) return;
    const next = event.relatedTarget;
    if (!(next instanceof Node)) return;
    if (panel.contains(next) || panel.closest('anchored-popover-component')?.contains(next)) return;
    try {
      if (panel.matches(':popover-open')) panel.hidePopover();
    } catch (_) {
      // No Popover API: nothing to close.
    }
  },
  true
);

document.addEventListener('theme-drawer:open', () => {
  for (const panel of document.querySelectorAll(PANEL_SELECTOR)) {
    try {
      if (panel instanceof HTMLElement && panel.matches(':popover-open')) panel.hidePopover();
    } catch (_) {
      // Browsers without the Popover API (and without the polyfill) have nothing to close.
    }
  }
});

customElements.whenDefined('shopify-chat').then(() => {
  for (const row of document.querySelectorAll('.hdr-help__item--chat')) {
    row.removeAttribute('hidden');
  }
});
